import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { refreshAssetCache } from '@/lib/assetCache';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Safety: never allow the app to be stuck in a perpetual "loading" state.
    const initTimeoutId = window.setTimeout(() => {
      setLoading(false);
    }, 8000);

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        window.clearTimeout(initTimeoutId);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Track last active on any auth event when user is present
        if (session?.user) {
          const isElectron = typeof window !== "undefined" && (window.electronAPI !== undefined || window.navigator.userAgent.includes("Electron"));
          const platform = isElectron
            ? 'desktop'
            : Capacitor.isNativePlatform()
              ? Capacitor.getPlatform() === 'ios' ? 'ios' : 'android'
              : 'web';
          supabase.rpc('update_last_active', { _platform: platform }).then();

          // Pre-populate the desktop asset cache so the Quick Access shortcut
          // window has data available immediately, even before the user
          // navigates to any asset view.
          if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
            void refreshAssetCache();
          }
        }
      }
    );

    // THEN check for existing session
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        window.clearTimeout(initTimeoutId);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      })
      .catch(() => {
        window.clearTimeout(initTimeoutId);
        // Never deadlock the app if session restoration fails
        setSession(null);
        setUser(null);
        setLoading(false);
      });

    // On native platforms, listen for deep link redirects from OAuth
    let appUrlListener: { remove: () => void } | undefined;
    if (Capacitor.isNativePlatform()) {
      import('@capacitor/app').then(({ App: CapApp }) => {
        CapApp.addListener('appUrlOpen', async ({ url }) => {
          console.log('[OAuth] appUrlOpen received:', url);
          
          // Close the in-app browser first
          try { await Browser.close(); } catch {}

          try {
            // Handle tokens in the URL fragment (hash) — implicit flow
            const hashIndex = url.indexOf('#');
            if (hashIndex > -1) {
              const params = new URLSearchParams(url.substring(hashIndex + 1));
              const accessToken = params.get('access_token');
              const refreshToken = params.get('refresh_token');
              if (accessToken && refreshToken) {
                console.log('[OAuth] Setting session from tokens');
                await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
                return;
              }
            }

            // Handle authorization code flow (code in query or path)
            // Try parsing as a proper URL first
            let code: string | null = null;
            try {
              // Replace custom scheme with https so URL parser works
              const parseable = url.replace(/^app\.testassets:\/\//, 'https://callback.local/');
              const urlObj = new URL(parseable);
              code = urlObj.searchParams.get('code');
            } catch {
              // Fallback: extract code from raw string
              const codeMatch = url.match(/[?&]code=([^&#]+)/);
              if (codeMatch) code = decodeURIComponent(codeMatch[1]);
            }

            if (code) {
              console.log('[OAuth] Exchanging code for session');
              const { error } = await supabase.auth.exchangeCodeForSession(code);
              if (error) {
                console.error('[OAuth] Code exchange failed, retrying once:', error.message);
                // Retry once after a short delay — intermittent network issues
                await new Promise(r => setTimeout(r, 500));
                const { error: retryError } = await supabase.auth.exchangeCodeForSession(code);
                if (retryError) {
                  console.error('[OAuth] Code exchange retry also failed:', retryError.message);
                }
              }
            }
          } catch (err) {
            console.error('[OAuth] Error processing callback:', err);
          }
        }).then(listener => { appUrlListener = listener; });
      });
    }

    return () => {
      window.clearTimeout(initTimeoutId);
      subscription.unsubscribe();
      appUrlListener?.remove();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/app`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { error };
  };

  const signOut = async () => {
    // Clear local state immediately
    setSession(null);
    setUser(null);
    // Sign out from Supabase with global scope to invalidate all sessions
    await supabase.auth.signOut({ scope: 'global' });
    // In Electron, go to /auth since the landing page isn't useful;
    // on web/mobile, go to the landing page.
    const isElectron = typeof window !== "undefined" &&
      (window.electronAPI !== undefined || window.navigator.userAgent.includes("Electron"));
    navigate(isElectron ? '/auth' : '/');
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
